import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getProjects,
  createProject,
  updateProject,
  deleteProject,
} from "../api";
import API from "../api";

const EMPTY_FORM = {
  title: "", description: "", category: "Mobile App",
  image: "", github: "", live: "", apkLink: "",
  techStack: "", featured: false,
};

const EMPTY_TASK = {
  title: "", description: "", domain: "", day: "", resources: "", isActive: true,
};

const EMPTY_STUDENT = {
  fullName: "", email: "", phone: "", collegeName: "", domain: "",
  status: "Pending", certificateStatus: "Not Issued", certificateId: "", issuedAt: "",
  startDate: "", endDate: "", issuedDate: "",
};

const STATUS_COLORS = {
  Pending:   { bg: "#2b1a00", color: "#f7c948" },
  Active:    { bg: "#0d2b1d", color: "#2cb67d" },
  Completed: { bg: "#0d1a2b", color: "#00c6ff" },
  Revoked:   { bg: "#2b0d0d", color: "#ff4444" },
};

const CERT_COLORS = {
  "Not Issued": { bg: "#1a1a2e", color: "#888" },
  Valid:        { bg: "#0d2b1d", color: "#2cb67d" },
  Revoked:      { bg: "#2b0d0d", color: "#ff4444" },
};

const SUB_COLORS = {
  Submitted: { bg: "#2b1a00", color: "#f7c948" },
  Approved:  { bg: "#0d2b1d", color: "#2cb67d" },
  Rejected:  { bg: "#2b0d0d", color: "#ff4444" },
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("projects");
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  return (
    <div style={{ paddingTop: 80, paddingBottom: 80 }}>
      <div className="container">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800 }}>Admin Dashboard</h1>
          <button className="btn btn-outline" onClick={handleLogout}>Logout</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 32, borderBottom: "1px solid #1e1e2e", paddingBottom: 0, overflowX: "auto" }}>
          {[
            { key: "projects", label: "📁 Projects" },
            { key: "internships", label: "🎓 Students" },
            { key: "tasks", label: "📋 Tasks" },
            { key: "submissions", label: "✅ Submissions" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                background: "none",
                border: "none",
                borderBottom: tab === t.key ? "2px solid #7f5af0" : "2px solid transparent",
                color: tab === t.key ? "#7f5af0" : "#888",
                fontWeight: 600,
                fontSize: 14,
                padding: "10px 20px",
                cursor: "pointer",
                marginBottom: -1,
                whiteSpace: "nowrap",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "projects" && <ProjectsTab />}
        {tab === "internships" && <InternshipsTab />}
        {tab === "tasks" && <TasksTab />}
        {tab === "submissions" && <SubmissionsTab />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// PROJECTS TAB
// ─────────────────────────────────────────
function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [toast, setToast] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchProjects = async () => {
    try {
      const res = await getProjects();
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch {
      showToast("Failed to load projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      techStack: form.techStack.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editId) {
        await updateProject(editId, payload);
        showToast("Project updated!");
      } else {
        await createProject(payload);
        showToast("Project added!");
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditId(null);
      fetchProjects();
    } catch (err) {
      showToast("Error: " + (err.response?.data?.message || "Something went wrong"));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (p) => {
    setForm({ ...p, techStack: p.techStack?.join(", ") || "" });
    setEditId(p._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await deleteProject(id);
      showToast("Project deleted.");
      fetchProjects();
    } catch {
      showToast("Failed to delete.");
    }
  };

  const formFields = [
    { name: "title", label: "Project Title *", placeholder: "e.g. CrashGuard", type: "text", required: true },
    { name: "description", label: "Description *", placeholder: "What does this project do?", type: "textarea", required: true },
    { name: "image", label: "Image URL", placeholder: "https://...", type: "text" },
    { name: "github", label: "GitHub Link", placeholder: "https://github.com/...", type: "text" },
    { name: "live", label: "Live Demo URL", placeholder: "https://...", type: "text" },
    { name: "apkLink", label: "APK Download Link", placeholder: "https://...", type: "text" },
    { name: "techStack", label: "Tech Stack (comma separated)", placeholder: "React, Node.js, MongoDB", type: "text" },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <p style={{ color: "var(--muted)", fontSize: 14 }}>{projects.length} project{projects.length !== 1 ? "s" : ""}</p>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setEditId(null); setShowForm(!showForm); }}>
          {showForm ? "Cancel" : "+ Add Project"}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 40 }}>
          <h2 style={{ fontWeight: 700, marginBottom: 24, fontSize: 18 }}>
            {editId ? "Edit Project" : "Add New Project"}
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              {formFields.map((f) => (
                <div key={f.name} style={{ gridColumn: f.type === "textarea" ? "1 / -1" : undefined }}>
                  <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 6 }}>{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} rows={3} required={f.required} style={{ resize: "vertical" }} />
                  ) : (
                    <input name={f.name} type={f.type} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} required={f.required} />
                  )}
                </div>
              ))}
              <div>
                <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 6 }}>Category</label>
                <select name="category" value={form.category} onChange={handleChange}>
                  {["Mobile App", "Web App", "IoT", "Other"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" id="featured" name="featured" checked={form.featured} onChange={handleChange} style={{ width: "auto", accentColor: "var(--accent)" }} />
                <label htmlFor="featured" style={{ fontSize: 14, cursor: "pointer" }}>Mark as Featured</label>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : editId ? "Update Project" : "Add Project"}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setEditId(null); }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="spinner" />
      ) : projects.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--muted)" }}>
          <p style={{ fontSize: 40, marginBottom: 12 }}>📁</p>
          <p>No projects yet. Click "+ Add Project" to get started.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {projects.map((p) => (
            <div key={p._id} className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, padding: "16px 20px" }}>
              <div style={{ display: "flex", gap: 14, alignItems: "center", flex: 1, minWidth: 0 }}>
                <div style={{ width: 44, height: 44, borderRadius: 8, flexShrink: 0, background: "var(--bg2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                  {p.category === "Mobile App" ? "📱" : p.category === "IoT" ? "🔌" : "🌐"}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                    {p.title}
                    {p.featured && <span style={{ background: "var(--accent)", color: "#000", fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4 }}>FEATURED</span>}
                  </div>
                  <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 2 }}>
                    {p.category} · {p.techStack?.join(", ") || "No tech stack"}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px" }} onClick={() => handleEdit(p)}>Edit</button>
                <button className="btn btn-danger" style={{ fontSize: 13, padding: "6px 14px" }} onClick={() => handleDelete(p._id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </>
  );
}

// ─────────────────────────────────────────
// INTERNSHIPS TAB
// ─────────────────────────────────────────
function InternshipsTab() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_STUDENT);
  const [editId, setEditId] = useState(null);
  const [filterDomain, setFilterDomain] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterDomain) params.append("domain", filterDomain);
      if (filterStatus) params.append("status", filterStatus);
      const res = await API.get(`/internships?${params.toString()}`);
      setStudents(res.data.students || []);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to load students.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStudents(); }, [filterDomain, filterStatus]);

  const updateStudent = async (id, data) => {
    setSaving(true);
    try {
      await API.patch(`/internships/${id}`, data);
      showToast("Updated successfully!");
      fetchStudents();
      setSelected(null);
      setEditId(null);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  const deleteStudent = async (id) => {
    if (!window.confirm("Are you sure? This action cannot be undone.")) return;
    try {
      await API.delete(`/internships/${id}`);
      showToast("Student deleted successfully!");
      fetchStudents();
      setSelected(null);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete.");
    }
  };

  const handleEditClick = (student) => {
    setEditForm(student);
    setEditId(student._id);
  };

  const handleCancelEdit = () => {
    setEditForm(EMPTY_STUDENT);
    setEditId(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updateData = {
        fullName: editForm.fullName,
        email: editForm.email,
        phone: editForm.phone,
        collegeName: editForm.collegeName,
        domain: editForm.domain,
        status: editForm.status,
        certificateStatus: editForm.certificateStatus,
        certificateId: editForm.certificateId,
      };
      if (editForm.issuedAt) updateData.issuedAt = editForm.issuedAt;
      if (editForm.startDate) updateData.startDate = editForm.startDate;
      if (editForm.endDate) updateData.endDate = editForm.endDate;
      if (editForm.issuedDate) updateData.issuedDate = editForm.issuedDate;
      
      await API.patch(`/internships/${editId}`, updateData);
      showToast("Student updated successfully!");
      fetchStudents();
      setEditId(null);
      setEditForm(EMPTY_STUDENT);
      setSelected(null);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12, marginBottom: 28 }}>
        {[
          { label: "Total", value: students.length, color: "#7f5af0" },
          { label: "Pending", value: students.filter(s => s.status === "Pending").length, color: "#f7c948" },
          { label: "Active", value: students.filter(s => s.status === "Active").length, color: "#2cb67d" },
          { label: "Completed", value: students.filter(s => s.status === "Completed").length, color: "#00c6ff" },
        ].map((s) => (
          <div key={s.label} className="card" style={{ padding: "16px", textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <select value={filterDomain} onChange={(e) => setFilterDomain(e.target.value)} style={{ fontSize: 13, padding: "8px 12px", background: "#111118", border: "1px solid #1e1e2e", color: "#e8e8f0", borderRadius: 8 }}>
          <option value="">All Domains</option>
          {["Full Stack Development", "Python Development", "Java Development", "Web App Building", "AI Tools & Automation"].map(d => (
            <option key={d}>{d}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ fontSize: 13, padding: "8px 12px", background: "#111118", border: "1px solid #1e1e2e", color: "#e8e8f0", borderRadius: 8 }}>
          <option value="">All Status</option>
          {["Pending", "Active", "Completed", "Revoked"].map(s => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <button className="btn btn-outline" style={{ fontSize: 13, padding: "8px 16px" }} onClick={fetchStudents}>
          Refresh
        </button>
      </div>

      {/* Student List */}
      {loading ? (
        <div className="spinner" />
      ) : students.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--muted)" }}>
          <p style={{ fontSize: 40, marginBottom: 12 }}>🎓</p>
          <p>No students registered yet.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {students.map((s) => (
            <div key={s._id} className="card" style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{s.fullName}</div>
                <div style={{ color: "#888", fontSize: 12, marginTop: 3 }}>
                  {s.email} · {s.phone}
                </div>
                <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>
                  {s.domain} · {s.collegeName}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 100, fontWeight: 600, background: STATUS_COLORS[s.status]?.bg, color: STATUS_COLORS[s.status]?.color }}>
                    {s.status}
                  </span>
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 100, fontWeight: 600, background: CERT_COLORS[s.certificateStatus]?.bg, color: CERT_COLORS[s.certificateStatus]?.color }}>
                    Cert: {s.certificateStatus}
                  </span>
                  {s.certificateId && (
                    <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 100, background: "#1a1a2e", color: "#00c6ff", fontFamily: "monospace", letterSpacing: 1 }}>
                      {s.certificateId}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="btn btn-outline"
                  style={{ fontSize: 13, padding: "6px 16px" }}
                  onClick={() => setSelected(s)}
                >
                  View
                </button>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 13, padding: "6px 16px" }}
                  onClick={() => { setSelected(s); handleEditClick(s); }}
                >
                  Edit
                </button>
                <button
                  className="btn btn-danger"
                  style={{ fontSize: 13, padding: "6px 16px" }}
                  onClick={() => deleteStudent(s._id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Student Detail Modal */}
      {selected && !editId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#111118", border: "1px solid #1e1e2e", borderRadius: 16, padding: 28, maxWidth: 500, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontWeight: 700, fontSize: 18 }}>Student Details</h2>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: "#888", fontSize: 22, cursor: "pointer" }}>×</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
              <Detail label="Name" value={selected.fullName} />
              <Detail label="Email" value={selected.email} />
              <Detail label="Phone" value={selected.phone} />
              <Detail label="College" value={selected.collegeName} />
              <Detail label="Domain" value={selected.domain} />
              <Detail label="Status" value={selected.status} />
              <Detail label="Certificate Status" value={selected.certificateStatus} />
              <Detail label="Certificate ID" value={selected.certificateId} mono />
              {selected.issuedAt && <Detail label="Issued At" value={new Date(selected.issuedAt).toLocaleDateString()} />}
              {selected.githubProfile && <Detail label="GitHub" value={<a href={selected.githubProfile} target="_blank" rel="noreferrer" style={{ color: "#7f5af0" }}>View →</a>} />}
              {selected.linkedinProfile && <Detail label="LinkedIn" value={<a href={selected.linkedinProfile} target="_blank" rel="noreferrer" style={{ color: "#7f5af0" }}>View →</a>} />}
              {selected.paymentScreenshotUrl && (
                <div>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Payment Screenshot</div>
                  <a href={`https://porfolio-backend-production-aab2.up.railway.app${selected.paymentScreenshotUrl}`} target="_blank" rel="noreferrer" style={{ color: "#7f5af0", fontSize: 13 }}>View Screenshot →</a>
                </div>
              )}
            </div>

            <button className="btn btn-outline" onClick={() => setSelected(null)} style={{ width: "100%" }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Student Edit Modal */}
      {editId && selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#111118", border: "1px solid #1e1e2e", borderRadius: 16, padding: 28, maxWidth: 500, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontWeight: 700, fontSize: 18 }}>Edit Student</h2>
              <button onClick={handleCancelEdit} style={{ background: "none", border: "none", color: "#888", fontSize: 22, cursor: "pointer" }}>×</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
              {[
                { name: "fullName", label: "Full Name" },
                { name: "email", label: "Email" },
                { name: "phone", label: "Phone" },
                { name: "collegeName", label: "College Name" },
                { name: "domain", label: "Domain" },
                { name: "certificateId", label: "Certificate ID" },
                { name: "issuedAt", label: "Issued At (YYYY-MM-DD)", type: "date" },
                { name: "startDate", label: "Internship Start Date (YYYY-MM-DD)", type: "date" },
                { name: "endDate", label: "Internship End Date (YYYY-MM-DD)", type: "date" },
                { name: "issuedDate", label: "Certificate Issued Date (YYYY-MM-DD)", type: "date" },
              ].map(f => (
                <div key={f.name}>
                  <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>{f.label}</label>
                  <input
                    type={f.type || "text"}
                    name={f.name}
                    value={editForm[f.name] || ""}
                    onChange={handleEditChange}
                    style={{ width: "100%", fontSize: 13, padding: "8px 12px" }}
                  />
                </div>
              ))}
              
              <div>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Status</label>
                <select name="status" value={editForm.status} onChange={handleEditChange} style={{ width: "100%", fontSize: 13, padding: "8px 12px" }}>
                  {["Pending", "Active", "Completed", "Revoked"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Certificate Status</label>
                <select name="certificateStatus" value={editForm.certificateStatus} onChange={handleEditChange} style={{ width: "100%", fontSize: 13, padding: "8px 12px" }}>
                  {["Not Issued", "Valid", "Revoked"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving} style={{ flex: 1, opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button className="btn btn-outline" onClick={handleCancelEdit} style={{ flex: 1 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </>
  );
}

// ─────────────────────────────────────────
// TASKS TAB
// ─────────────────────────────────────────
function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [form, setForm] = useState(EMPTY_TASK);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await API.get("/tasks/admin");
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to load tasks.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.domain || !form.day) {
      showToast("Please fill all required fields.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        day: Number(form.day),
        resources: form.resources || "",
      };
      if (editId) {
        await API.patch(`/tasks/${editId}`, payload);
        showToast("Task updated!");
      } else {
        await API.post("/tasks", payload);
        showToast("Task created!");
      }
      setForm(EMPTY_TASK);
      setEditId(null);
      setShowForm(false);
      fetchTasks();
    } catch (err) {
      showToast(err.response?.data?.message || "Error saving task.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (task) => {
    setForm({
      ...task,
      resources: Array.isArray(task.resources)
        ? task.resources.join(", ")
        : task.resources || "",
    });
    setEditId(task._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await API.delete(`/tasks/${id}`);
      showToast("Task deleted.");
      fetchTasks();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete.");
    }
  };

  const handleCancel = () => {
    setForm(EMPTY_TASK);
    setEditId(null);
    setShowForm(false);
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <p style={{ color: "var(--muted)", fontSize: 14 }}>{tasks.length} task{tasks.length !== 1 ? "s" : ""}</p>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_TASK); setEditId(null); setShowForm(!showForm); }}>
          {showForm ? "Cancel" : "+ Create Task"}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 40 }}>
          <h2 style={{ fontWeight: 700, marginBottom: 24, fontSize: 18 }}>
            {editId ? "Edit Task" : "Create New Task"}
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div>
                <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 6 }}>Title *</label>
                <input name="title" value={form.title} onChange={handleChange} placeholder="Task title" required />
              </div>
              <div>
                <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 6 }}>Description *</label>
                <textarea name="description" value={form.description} onChange={handleChange} placeholder="Task description" rows={3} required style={{ resize: "vertical" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <div>
                  <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 6 }}>Domain *</label>
                  <select name="domain" value={form.domain} onChange={handleChange} required>
                    <option value="">Select Domain</option>
                    {["Full Stack Development", "Python Development", "Java Development", "Web App Building", "AI Tools & Automation"].map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 6 }}>Task No *</label>
                  <input name="day" type="number" value={form.day} onChange={handleChange} placeholder="Task (1-30)" required min="1" max="30" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 6 }}>Resources (comma separated)</label>
                <input name="resources" value={form.resources} onChange={handleChange} placeholder="https://link1.com, https://link2.com" />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" id="isActive" name="isActive" checked={form.isActive} onChange={handleChange} style={{ width: "auto", accentColor: "var(--accent)" }} />
                <label htmlFor="isActive" style={{ fontSize: 14, cursor: "pointer" }}>Active</label>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : editId ? "Update Task" : "Create Task"}
              </button>
              <button type="button" className="btn btn-outline" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="spinner" />
      ) : tasks.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--muted)" }}>
          <p style={{ fontSize: 40, marginBottom: 12 }}>📋</p>
          <p>No tasks created yet. Click "+ Create Task" to get started.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {tasks.map((task) => (
            <div key={task._id} className="card" style={{ padding: "16px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span style={{ background: "#1a1a2e", color: "#7f5af0", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 100 }}>
                      Task {task.day}
                    </span>
                    <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{task.title}</h3>
                    {!task.isActive && <span style={{ background: "#2b0d0d", color: "#ff4444", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 100 }}>INACTIVE</span>}
                  </div>
                  <p style={{ color: "#888", fontSize: 13, marginBottom: 8 }}>{task.description}</p>
                  <div style={{ color: "#555", fontSize: 12 }}>
                    <strong>Domain:</strong> {task.domain}
                  </div>
                  {task.resources && (
                    <div style={{ color: "#555", fontSize: 12, marginTop: 4 }}>
                      <strong>Resources:</strong>{" "}
                      {(Array.isArray(task.resources)
                        ? task.resources
                        : String(task.resources).split(",")
                      )
                        .map((r) => r.trim())
                        .filter(Boolean)
                        .map((r, i) => (
                          <a
                            key={i}
                            href={r}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: "#7f5af0", marginLeft: 4 }}
                          >
                            Link {i + 1}
                          </a>
                        ))}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px" }} onClick={() => handleEdit(task)}>Edit</button>
                  <button className="btn btn-danger" style={{ fontSize: 13, padding: "6px 14px" }} onClick={() => handleDelete(task._id)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </>
  );
}

// ─────────────────────────────────────────
// SUBMISSIONS TAB
// ─────────────────────────────────────────
function SubmissionsTab() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [actioningId, setActioningId] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await API.get("/tasks/submissions");
      setSubmissions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubmissions(); }, []);

  const updateSubmission = async (id, status) => {
    setActioningId(id);
    try {
      await API.patch(`/tasks/submissions/${id}`, { status });
      showToast(`Submission ${status.toLowerCase()}!`);
      fetchSubmissions();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update.");
    } finally {
      setActioningId(null);
    }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <p style={{ color: "var(--muted)", fontSize: 14 }}>{submissions.length} submission{submissions.length !== 1 ? "s" : ""}</p>
        <button className="btn btn-outline" onClick={fetchSubmissions} style={{ fontSize: 13, padding: "8px 16px" }}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : submissions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--muted)" }}>
          <p style={{ fontSize: 40, marginBottom: 12 }}>✅</p>
          <p>No submissions yet.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {submissions.map((sub) => {
            const task = sub.task || sub.taskId;
            const student = sub.student || sub.studentId;
            const submissionLink = sub.submissionLink || sub.submissionUrl;
            return (
            <div key={sub._id} className="card" style={{ padding: "16px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                    {task?.title || "Unknown Task"}
                  </div>
                  <div style={{ color: "#888", fontSize: 12, marginBottom: 4 }}>
                    Student: {student?.fullName || "Unknown"}
                  </div>
                  <div style={{ color: "#888", fontSize: 12, marginBottom: 4 }}>
                    Email: {student?.email || "—"}
                  </div>
                  <div style={{ color: "#888", fontSize: 12, marginBottom: 4 }}>
                    Domain: {task?.domain || student?.domain || "—"}
                  </div>
                  <div style={{ color: "#888", fontSize: 12, marginBottom: 4 }}>
                    Task No: {task?.day || "—"}
                  </div>
                  {submissionLink && (
                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                      <a href={submissionLink} target="_blank" rel="noreferrer" style={{ color: "#7f5af0" }}>
                        View Submission →
                      </a>
                    </div>
                  )}
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 100, fontWeight: 600, background: SUB_COLORS[sub.status]?.bg, color: SUB_COLORS[sub.status]?.color }}>
                    {sub.status}
                  </span>
                </div>
                {sub.status === "Submitted" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: 13, padding: "6px 14px" }}
                      onClick={() => updateSubmission(sub._id, "Approved")}
                      disabled={actioningId === sub._id}
                    >
                      Approve
                    </button>
                    <button
                      className="btn btn-danger"
                      style={{ fontSize: 13, padding: "6px 14px" }}
                      onClick={() => updateSubmission(sub._id, "Rejected")}
                      disabled={actioningId === sub._id}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13 }}>
      <span style={{ color: "#555", flexShrink: 0 }}>{label}</span>
      <span style={{ color: "#c0c0e0", textAlign: "right", fontFamily: mono ? "monospace" : "inherit", letterSpacing: mono ? 1 : 0 }}>{value || "—"}</span>
    </div>
  );
}